/**
 * Motors — the shared vocabulary for car dealerships and car hire firms.
 *
 * THE decision this module encodes: **a product row is one physical car.**
 * Not a model, not a class. A room type has eight identical units, so
 * accommodation splits `products` (the type) from `roomUnits` (the rooms). A
 * car cannot be split that way — two Harriers on the same lot have different
 * mileage, colour, chassis numbers and price, and only one has a sunroof.
 * The thing a dealer photographs, prices and sells is the individual car. So
 * the product IS the unit: `products.motors`, stock 1, and hire availability
 * keys straight off `productId`. There is deliberately no vehicle-units table.
 *
 * One car, two ways to earn: `forSale` (asking price on `products.price`) and
 * `forHire` (a rate card in `motors.hire`). Either, both, or neither while it
 * is still on the ship.
 *
 * Plain TypeScript (one type import from the business-kind registry) —
 * imported by both the React app and Convex (`convex/motors/*` reach it via a
 * relative path).
 */

import type { BusinessKind } from "./businessKinds";

export const MOTORS_INDUSTRY_SLUG = "motors" as const;
export const MOTORS_KIND: BusinessKind = "motors" as BusinessKind;

// ── The vehicle record ───────────────────────────────────────────────────

export type VehicleStatus =
  | "in_transit"
  | "in_bond"
  | "available"
  | "reserved"
  | "sold"
  | "not_listed";

export type VehicleCondition = "brand_new" | "foreign_used" | "locally_used";

export interface LandedCost {
  purchase?: number;
  freight?: number;
  duty?: number;
  vat?: number;
  clearing?: number;
  other?: number;
}

export interface HireRates {
  dailyRate: number;
  weeklyRate?: number;
  monthlyRate?: number;
  /** ADDED to the day rate when the customer takes a driver. */
  withDriverRate?: number;
  deposit?: number;
  fuelPolicy?: string;
  mileageCapKm?: number;
  excessPerKm?: number;
  minDays?: number;
}

export interface MotorsDetails {
  make: string;
  model: string;
  trim?: string;
  yearMade: number;
  yearRegistered?: number;
  bodyType?: string;
  /** Storefront grouping for hire — customers browse a class, not a plate. */
  class?: string;
  colour?: string;
  interiorColour?: string;

  chassisNumber?: string;
  engineNumber?: string;
  plate?: string;

  engineCc?: number;
  fuel?: string;
  transmission?: string;
  drive?: string;
  seats?: number;
  doors?: number;
  odometerKm?: number;
  odometerVerified?: boolean;

  condition?: VehicleCondition;
  auctionGrade?: string;
  accidentNote?: string;
  originCountry?: string;
  arrivalDate?: number;

  landedCost?: LandedCost;

  logbookPresent?: boolean;
  logbookName?: string;
  transferDone?: boolean;

  forSale?: boolean;
  /** INTERNAL — the lowest the owner will take. Never leaves the till. */
  floorPrice?: number;
  currency?: string;
  negotiable?: boolean;
  consignmentOwner?: string;
  consignmentCommissionPct?: number;

  forHire?: boolean;
  hire?: HireRates;

  insuranceExpiry?: number;
  permitExpiry?: number;
  serviceDueDate?: number;
  serviceDueKm?: number;
  trackerFitted?: boolean;

  status?: VehicleStatus;
  /** When it went on the lot — the days-on-the-lot clock. */
  listedAt?: number;
  soldAt?: number;
}

// ── Pick lists ───────────────────────────────────────────────────────────

export const BODY_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV" },
  { value: "hatchback", label: "Hatchback" },
  { value: "wagon", label: "Station wagon" },
  { value: "pickup", label: "Pickup" },
  { value: "van", label: "Van / minibus" },
  { value: "bus", label: "Bus / coaster" },
  { value: "truck", label: "Truck" },
  { value: "coupe", label: "Coupe" },
];

/** How hire customers browse: by what the car is FOR, not by its badge. */
export const VEHICLE_CLASSES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "economy", label: "Economy" },
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV / 4x4" },
  { value: "seven_seater", label: "7-seater" },
  { value: "van", label: "Van" },
  { value: "pickup", label: "Pickup" },
  { value: "bus", label: "Bus / coaster" },
  { value: "luxury", label: "Luxury" },
];

export const FUELS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "petrol", label: "Petrol" },
  { value: "diesel", label: "Diesel" },
  { value: "hybrid", label: "Hybrid" },
  { value: "electric", label: "Electric" },
];

export const TRANSMISSIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "automatic", label: "Automatic" },
  { value: "manual", label: "Manual" },
  { value: "cvt", label: "CVT" },
];

export const DRIVES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "2wd", label: "2WD" },
  { value: "4wd", label: "4WD" },
  { value: "awd", label: "AWD" },
];

export const CONDITIONS: ReadonlyArray<{ value: VehicleCondition; label: string; hint: string }> = [
  { value: "brand_new", label: "Brand new", hint: "Zero mileage, from the maker" },
  { value: "foreign_used", label: "Foreign used", hint: "Imported, used abroad" },
  { value: "locally_used", label: "Locally used", hint: "Already used in the country" },
];

export const FUEL_POLICIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "full_to_full", label: "Full to full" },
  { value: "same_to_same", label: "Return as received" },
  { value: "included", label: "Fuel included" },
];

export const VEHICLE_STATUS_FLOW: ReadonlyArray<{ value: VehicleStatus; label: string; tone: string }> = [
  { value: "in_transit", label: "In transit", tone: "#8b5cf6" },
  { value: "in_bond", label: "In bond", tone: "#6366f1" },
  { value: "available", label: "Available", tone: "#22c55e" },
  { value: "reserved", label: "Reserved", tone: "#f59e0b" },
  { value: "sold", label: "Sold", tone: "#64748b" },
  { value: "not_listed", label: "Not listed", tone: "#94a3b8" },
];

export type HireStatus = "booked" | "out" | "returned" | "cancelled" | "no_show";

export const HIRE_FLOW: ReadonlyArray<{
  value: HireStatus;
  label: string;
  next?: HireStatus;
  action?: string;
  tone: string;
}> = [
  { value: "booked", label: "Booked", next: "out", action: "Hand over", tone: "#3b82f6" },
  { value: "out", label: "Out", next: "returned", action: "Take back", tone: "#f59e0b" },
  { value: "returned", label: "Returned", tone: "#22c55e" },
  { value: "cancelled", label: "Cancelled", tone: "#ef4444" },
  { value: "no_show", label: "No-show", tone: "#94a3b8" },
];

/** Platform categories for the industry — seeded once by motors/seed.ts. */
export const MOTORS_CATEGORY_SEED: ReadonlyArray<{
  name: string;
  slug: string;
  icon: string;
  description: string;
}> = [
  { name: "Toyota", slug: "toyota", icon: "🚗", description: "Harrier, Noah, Premio, Land Cruiser, Hiace" },
  { name: "Nissan", slug: "nissan", icon: "🚗", description: "X-Trail, Note, Patrol, Caravan" },
  { name: "Subaru", slug: "subaru", icon: "🚗", description: "Forester, Outback, Impreza" },
  { name: "Mitsubishi", slug: "mitsubishi", icon: "🚗", description: "Pajero, Outlander, Canter" },
  { name: "Honda", slug: "honda", icon: "🚗", description: "Fit, CR-V, Stream" },
  { name: "Mazda", slug: "mazda", icon: "🚗", description: "Demio, CX-5, Axela" },
  { name: "Isuzu", slug: "isuzu", icon: "🚚", description: "D-Max, Forward, Elf" },
  { name: "Land Rover", slug: "land-rover", icon: "🚙", description: "Discovery, Defender, Range Rover" },
  { name: "Mercedes-Benz", slug: "mercedes-benz", icon: "🚘", description: "C-Class, E-Class, Sprinter" },
  { name: "BMW", slug: "bmw", icon: "🚘", description: "3 Series, 5 Series, X5" },
  { name: "Volkswagen", slug: "volkswagen", icon: "🚗", description: "Golf, Touareg, Passat" },
  { name: "Suzuki", slug: "suzuki", icon: "🚗", description: "Swift, Escudo, Alto" },
  { name: "Hyundai", slug: "hyundai", icon: "🚗", description: "Tucson, Santa Fe, Elantra" },
  { name: "Kia", slug: "kia", icon: "🚗", description: "Sportage, Sorento, Rio" },
  { name: "Ford", slug: "ford", icon: "🚙", description: "Ranger, Everest, Escape" },
  { name: "Other makes", slug: "other-makes", icon: "🚗", description: "Anything not listed above" },
];

export const VEHICLE_PLACEHOLDER_IMAGE = "/images/placeholder-product.jpg";

/** The named photo slots a listing wants. The odometer shot is the one buyers zoom into. */
export const VEHICLE_PHOTO_SLOTS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "front", label: "Front 3/4" },
  { key: "rear", label: "Rear 3/4" },
  { key: "side", label: "Side" },
  { key: "interior", label: "Interior" },
  { key: "dashboard", label: "Dashboard" },
  { key: "odometer", label: "Odometer" },
  { key: "engine", label: "Engine bay" },
  { key: "boot", label: "Boot" },
];

// ── Predicates and derivations ───────────────────────────────────────────

/** A product that renders and sells as a vehicle. */
export function isVehicleProduct(product: any): boolean {
  if (!product) return false;
  return product.industry === MOTORS_INDUSTRY_SLUG || !!product.motors;
}

export function isForSale(product: any): boolean {
  return !!product?.motors && product.motors.forSale !== false;
}

export function isForHire(product: any): boolean {
  return !!product?.motors?.forHire && Number(product.motors.hire?.dailyRate) > 0;
}

/** "2018 Toyota Harrier Premium" — year first, the way the trade writes it. */
export function vehicleTitle(product: any): string {
  const m = product?.motors;
  if (!m) return String(product?.name || "Vehicle");
  return [m.yearMade, m.make, m.model, m.trim].filter(Boolean).join(" ");
}

/** "UAX 123A" when registered, otherwise what the car is instead. */
export function plateLabel(product: any): string {
  const m = product?.motors;
  if (m?.plate) return String(m.plate);
  if (m?.status === "in_transit") return "In transit";
  if (m?.status === "in_bond") return "In bond";
  return "Unregistered";
}

export function landedCostTotal(cost: LandedCost | undefined | null): number {
  if (!cost) return 0;
  return (
    (Number(cost.purchase) || 0) +
    (Number(cost.freight) || 0) +
    (Number(cost.duty) || 0) +
    (Number(cost.vat) || 0) +
    (Number(cost.clearing) || 0) +
    (Number(cost.other) || 0)
  );
}

/** Margin against the asking price. The number a dealer runs the business on. */
export function vehicleMargin(product: any): { cost: number; margin: number; pct: number } {
  const cost = landedCostTotal(product?.motors?.landedCost);
  const price = Number(product?.price) || 0;
  const margin = price - cost;
  return { cost, margin, pct: cost > 0 ? (margin / cost) * 100 : 0 };
}

/** How long it has sat on the lot. Past ninety days is money asleep. */
export function daysOnLot(product: any, nowMs: number = Date.now()): number | null {
  const from = Number(product?.motors?.listedAt || product?.createdAt || product?._creationTime);
  if (!from) return null;
  const end = Number(product?.motors?.soldAt) || nowMs;
  return Math.max(0, Math.floor((end - from) / 86400000));
}

export function conditionLabel(value: string | null | undefined): string {
  return CONDITIONS.find((c) => c.value === value)?.label ?? "";
}

export function statusLabel(value: string | null | undefined): string {
  return VEHICLE_STATUS_FLOW.find((s) => s.value === value)?.label ?? "";
}

export function pickLabel(list: ReadonlyArray<{ value: string; label: string }>, value: string | null | undefined): string {
  if (!value) return "";
  return list.find((x) => x.value === value)?.label ?? String(value);
}

/**
 * What a hire costs. Weekly and monthly rates are per-week / per-month
 * equivalents, applied when the run is long enough to earn them; anything
 * left over falls back to the day rate. The driver supplement is per day for
 * the whole run.
 */
export function hireQuote(
  rates: HireRates | undefined | null,
  days: number,
  withDriver: boolean,
): { total: number; perDay: number; driverTotal: number; basis: string } {
  const n = Math.max(1, Math.floor(days));
  const daily = Number(rates?.dailyRate) || 0;
  const weekly = Number(rates?.weeklyRate) || 0;
  const monthly = Number(rates?.monthlyRate) || 0;

  let base = daily * n;
  let basis = "daily";
  if (monthly > 0 && n >= 28) {
    const months = Math.floor(n / 28);
    const rest = n - months * 28;
    base = months * monthly + rest * daily;
    basis = "monthly";
  } else if (weekly > 0 && n >= 7) {
    const weeks = Math.floor(n / 7);
    const rest = n - weeks * 7;
    base = weeks * weekly + rest * daily;
    basis = "weekly";
  }

  const driverTotal = withDriver ? (Number(rates?.withDriverRate) || 0) * n : 0;
  const total = base + driverTotal;
  return { total, perDay: n > 0 ? Math.round(total / n) : total, driverTotal, basis };
}

/** Charges owed at the end of a hire that were not in the booking. */
export function returnCharges(input: {
  odometerOut?: number;
  odometerIn?: number;
  mileageCapKm?: number;
  excessPerKm?: number;
  days: number;
  lateDays?: number;
  ratePerDay?: number;
}): { excessKm: number; excessCharge: number; lateCharge: number } {
  const driven = Math.max(0, (Number(input.odometerIn) || 0) - (Number(input.odometerOut) || 0));
  const allowed = (Number(input.mileageCapKm) || 0) * Math.max(1, input.days);
  const excessKm = allowed > 0 ? Math.max(0, driven - allowed) : 0;
  const excessCharge = excessKm * (Number(input.excessPerKm) || 0);
  const lateCharge = Math.max(0, Number(input.lateDays) || 0) * (Number(input.ratePerDay) || 0);
  return { excessKm, excessCharge, lateCharge };
}
